import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
} from '@mui/material';
import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import CardGiftcardRounded from '@mui/icons-material/CardGiftcardRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import LockOpenRounded from '@mui/icons-material/LockOpenRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import { ApiError } from '@/api';
import { StatTile } from '@/components/data/StatTile';
import { useNotify } from '@/components/feedback/notificationsContext';
import { useAsync } from '@/hooks/useAsync';
import {
  blockInstitution,
  cancelPlan,
  endCourtesy,
  getAdminBilling,
  getAdminInstitutionBilling,
  getCatalog,
  grantCourtesy,
  unblockInstitution,
} from '@/platform/billing/api';
import {
  BillingPanel,
  ChangePlanDialog,
  ReasonDialog,
  StatusChip,
} from '@/platform/billing/BillingPanel';
import {
  CATEGORIES,
  CYCLE_SHORT,
  formatDate,
  formatMoney,
  ORGANIZATIONS,
} from '@/platform/billing/labels';
import type { InstitutionBilling, InstitutionBillingDetail } from '@/platform/billing/types';
import { useSession } from '@/platform/session';
import { PageHeader } from '../PageHeader';
import { AdminTabs } from './AdminPage';

type Filter = 'all' | 'paying' | 'courtesy' | 'trial' | 'attention';

const FILTERS: { key: Filter; label: string; test: (b: InstitutionBilling) => boolean }[] = [
  { key: 'all', label: 'Todas', test: () => true },
  {
    key: 'paying',
    label: 'Pagantes',
    test: (b) => b.subscription?.kind === 'paid' && ['active', 'overdue'].includes(b.status),
  },
  { key: 'courtesy', label: 'Cortesia', test: (b) => b.status === 'courtesy' },
  { key: 'trial', label: 'Em teste', test: (b) => b.status === 'trial' },
  {
    key: 'attention',
    label: 'Pedem atenção',
    test: (b) => !b.hasAccess || b.status === 'overdue' || b.blocked,
  },
];

const ORIGIN: Record<string, string> = {
  founding: 'Fundadora',
  admin: 'Cadastrada pela administração',
  self_service: 'Cadastro próprio',
};

/** Admins: who pays, who has a courtesy, who is late, and what to do about each one. */
export function BillingAdminPage() {
  const data = useAsync(getAdminBilling);
  const [filter, setFilter] = useState<Filter>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const d = data.data;
  const rows = useMemo(
    () => (d?.institutions ?? []).filter(FILTERS.find((f) => f.key === filter)!.test),
    [d, filter],
  );

  return (
    <>
      <PageHeader
        title="Administração"
        subtitle="Planos, cortesias e pagamentos das instituições."
        actions={
          <Button href="/planos" target="_blank" startIcon={<SellRounded />}>
            Página de planos
          </Button>
        }
      />
      <AdminTabs />
      {d?.mode === 'simulated' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>Pagamentos simulados:</b> a API está sem <code>ASAAS_API_KEY</code>. As faturas abrem
          uma página de teste com um botão “pagar”. Em produção, configure a chave e o webhook do
          Asaas.
        </Alert>
      )}
      {d?.mode === 'sandbox' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Asaas em <b>sandbox</b>: as cobranças são de teste.
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Receita recorrente (mês)"
            value={formatMoney(d?.summary.mrrCents ?? 0)}
            icon={TrendingUpRounded}
            loading={data.loading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Recebido (30 dias)"
            value={formatMoney(d?.summary.receivedMonthCents ?? 0)}
            icon={PaymentsRounded}
            loading={data.loading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Faturas em aberto"
            value={formatMoney(d?.summary.openCents ?? 0)}
            icon={ReceiptLongRounded}
            loading={data.loading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Instituições"
            value={d?.institutions.length ?? 0}
            icon={AccountBalanceRounded}
            loading={data.loading}
            footer={
              d && (
                <Typography variant="caption" color="text.secondary">
                  {d.summary.paying} pagantes · {d.summary.courtesy} cortesia · {d.summary.trial} em
                  teste
                  {d.summary.withoutAccess > 0 && ` · ${d.summary.withoutAccess} sem acesso`}
                </Typography>
              )
            }
          />
        </Grid>
      </Grid>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={filter}
        onChange={(_, f: Filter | null) => f && setFilter(f)}
        sx={{ mb: 2, flexWrap: 'wrap' }}
        aria-label="Filtrar instituições"
      >
        {FILTERS.map((f) => (
          <ToggleButton key={f.key} value={f.key} sx={{ px: 2 }}>
            {f.label}
            {d && ` (${d.institutions.filter(f.test).length})`}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {data.error ? (
        <Alert severity="error">Não foi possível carregar as assinaturas.</Alert>
      ) : (
        <TableContainer component={Card}>
          <Table sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell>Instituição</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell align="right">Estudantes</TableCell>
                <TableCell align="right">Valor/mês</TableCell>
                <TableCell>Acesso até</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.loading && !d
                ? [0, 1, 2].map((i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton height={32} />
                      </TableCell>
                    </TableRow>
                  ))
                : rows.map((b) => (
                    <TableRow
                      key={b.id}
                      hover
                      onClick={() => setOpenId(b.id)}
                      sx={{ cursor: 'pointer', opacity: b.isActive ? 1 : 0.6 }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {b.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ORIGIN[b.origin] ?? b.origin}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={b.status} blocked={b.blocked} />
                      </TableCell>
                      <TableCell>
                        {b.subscription ? (
                          <>
                            {b.subscription.planName}
                            <Typography variant="caption" color="text.secondary" component="div">
                              {b.subscription.kind === 'courtesy'
                                ? 'cortesia'
                                : CYCLE_SHORT[b.subscription.cycle]}
                            </Typography>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {b.students.toLocaleString('pt-BR')}
                        {b.seatLimit && (
                          <Typography variant="caption" color="text.secondary">
                            {' '}
                            / {b.seatLimit.toLocaleString('pt-BR')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {b.subscription?.kind === 'paid'
                          ? formatMoney(b.subscription.monthlyCents)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {b.accessUntil ? formatDate(b.accessUntil) : b.hasAccess ? 'sem fim' : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small">Gerenciar</Button>
                      </TableCell>
                    </TableRow>
                  ))}
              {d && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}
                  >
                    Nenhuma instituição neste filtro.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Drawer
        anchor="right"
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        slotProps={{ paper: { sx: { width: { xs: '100%', md: 760 }, p: { xs: 2, sm: 3 } } } }}
      >
        {openId && (
          <InstitutionBillingDrawer
            id={openId}
            onClose={() => setOpenId(null)}
            onChanged={() => data.reload()}
          />
        )}
      </Drawer>
    </>
  );
}

type Action = 'courtesy' | 'endCourtesy' | 'cancel' | 'block' | 'plan' | null;

function InstitutionBillingDrawer({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const notify = useNotify();
  const { refresh } = useSession();
  const detail = useAsync((signal) => getAdminInstitutionBilling(id, signal), [id]);
  const [action, setAction] = useState<Action>(null);
  const [endOn, setEndOn] = useState('');
  const [immediate, setImmediate] = useState(false);
  const b = detail.data;

  const done = (updated: InstitutionBillingDetail, message: string) => {
    detail.setData(() => updated);
    setAction(null);
    notify(message, 'success');
    onChanged();
    void refresh(); // the top bar lists only institutions with access
  };

  if (detail.error) return <Alert severity="error">Não foi possível carregar a instituição.</Alert>;
  if (!b) return <Skeleton variant="rounded" height={400} />;
  const sub = b.subscription;
  const paid = sub?.kind === 'paid' && !sub.canceledAt;
  const courtesy = b.status === 'courtesy';

  return (
    <>
      <Stack direction="row" sx={{ alignItems: 'center', mb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
          Assinatura
        </Typography>
        <IconButton aria-label="Fechar" onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </Stack>
      <BillingPanel
        billing={b}
        actions={
          <>
            <Button
              variant="tonal"
              startIcon={<CardGiftcardRounded />}
              onClick={() => setAction('courtesy')}
            >
              {courtesy ? 'Alterar cortesia' : 'Liberar por cortesia'}
            </Button>
            {courtesy && (
              <Button startIcon={<EventBusyRounded />} onClick={() => setAction('endCourtesy')}>
                Encerrar cortesia
              </Button>
            )}
            {!b.blocked && (
              <Button startIcon={<SellRounded />} onClick={() => setAction('plan')}>
                {paid ? 'Mudar plano' : 'Contratar plano'}
              </Button>
            )}
            {paid && (
              <Button
                color="error"
                startIcon={<HourglassBottomRounded />}
                onClick={() => setAction('cancel')}
              >
                Cancelar plano
              </Button>
            )}
            {b.blocked ? (
              <Button
                color="success"
                startIcon={<LockOpenRounded />}
                onClick={async () => {
                  try {
                    done(await unblockInstitution(b.id), 'Acesso restabelecido.');
                  } catch (err) {
                    notify(err instanceof ApiError ? err.message : 'Não foi possível.', 'error');
                  }
                }}
              >
                Restabelecer acesso
              </Button>
            ) : (
              <Button color="error" startIcon={<LockRounded />} onClick={() => setAction('block')}>
                Suspender acesso
              </Button>
            )}
          </>
        }
        extra={<ProfileCard billing={b} />}
      />

      <CourtesyDialog
        open={action === 'courtesy'}
        billing={b}
        onClose={() => setAction(null)}
        onDone={(updated) => done(updated, 'Cortesia registrada.')}
      />
      <ReasonDialog
        open={action === 'endCourtesy'}
        title={`Encerrar a cortesia de ${b.name}?`}
        description="Depois disso, estudantes, professores e coordenação deixam de entrar até a instituição contratar um plano. A coordenação vê a tela de contratação."
        confirmLabel={endOn ? 'Agendar encerramento' : 'Encerrar agora'}
        danger
        onClose={() => setAction(null)}
        onConfirm={async (reason) =>
          done(await endCourtesy(b.id, { on: endOn || null, reason }), 'Cortesia encerrada.')
        }
      >
        <TextField
          type="date"
          label="Último dia de acesso (vazio: hoje)"
          value={endOn}
          onChange={(e) => setEndOn(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </ReasonDialog>
      <ReasonDialog
        open={action === 'cancel'}
        title={`Cancelar o plano de ${b.name}?`}
        description="As cobranças futuras são canceladas no Asaas. Faturas já vencidas continuam em aberto."
        confirmLabel="Cancelar plano"
        danger
        onClose={() => setAction(null)}
        onConfirm={async (reason) =>
          done(await cancelPlan(b.id, { reason, immediate }), 'Plano cancelado.')
        }
      >
        <FormControlLabel
          control={<Switch checked={immediate} onChange={(e) => setImmediate(e.target.checked)} />}
          label={
            immediate
              ? 'Cortar o acesso agora'
              : `Manter o acesso até o fim do período pago (${formatDate(sub?.paidUntil ?? sub?.trialEndsAt ?? b.accessUntil)})`
          }
        />
      </ReasonDialog>
      <ReasonDialog
        open={action === 'block'}
        title={`Suspender o acesso de ${b.name}?`}
        description="Ninguém da instituição entra até você restabelecer. O plano e as cobranças não mudam (cancele o plano para parar de cobrar)."
        confirmLabel="Suspender"
        danger
        onClose={() => setAction(null)}
        onConfirm={async (reason) => done(await blockInstitution(b.id, reason), 'Acesso suspenso.')}
      />
      {action === 'plan' && (
        <ChangePlanDialog
          open
          billing={b}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            detail.reload();
            onChanged();
          }}
        />
      )}
    </>
  );
}

function CourtesyDialog({
  open,
  billing,
  onClose,
  onDone,
}: {
  open: boolean;
  billing: InstitutionBillingDetail;
  onClose: () => void;
  onDone: (updated: InstitutionBillingDetail) => void;
}) {
  const catalog = useAsync(getCatalog);
  const [plan, setPlan] = useState('campus');
  const [seats, setSeats] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      const sub = billing.subscription;
      setPlan(sub?.planKey && sub.planKey !== 'passe_enade' ? sub.planKey : 'campus');
      setSeats(sub?.kind === 'courtesy' && sub.seats ? String(sub.seats) : '');
      setUntil('');
      setReason('');
      setError(null);
    }
  }
  const paid = billing.subscription?.kind === 'paid' && !billing.subscription.canceledAt;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      onDone(
        await grantCourtesy(billing.id, {
          plan,
          seats: seats ? Number(seats) : null,
          until: until || null,
          reason: reason.trim(),
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível registrar a cortesia.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Liberar {billing.name} por cortesia</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {paid && (
            <Alert severity="warning">
              A instituição tem um plano pago: ele será cancelado no Asaas e as cobranças futuras
              param.
            </Alert>
          )}
          <TextField
            select
            label="Recursos do plano"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
          >
            {(catalog.data?.plans ?? [])
              .filter((p) => p.key !== 'passe_enade')
              .map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.name}
                </MenuItem>
              ))}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="number"
              label="Limite de estudantes"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              helperText="Vazio: sem limite."
              slotProps={{ htmlInput: { min: 1 } }}
              fullWidth
            />
            <TextField
              type="date"
              label="Até (último dia)"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              helperText="Vazio: sem data para terminar."
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Stack>
          <TextField
            label="Motivo (fica no histórico)"
            placeholder="Ex.: parceria acadêmica, piloto do 2º semestre"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={saving || reason.trim().length < 3}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Liberar por cortesia'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ProfileCard({ billing }: { billing: InstitutionBillingDetail }) {
  const p = billing.profile;
  if (!p.cnpj && !p.legalName && !p.contactName) {
    return (
      <Alert severity="info" variant="outlined">
        Sem dados cadastrais (instituição anterior ao cadastro próprio).
      </Alert>
    );
  }
  const rows: [string, string | null | undefined][] = [
    ['Razão social', p.legalName],
    ['CNPJ', p.cnpj],
    ['Código e-MEC', p.emecCode],
    ['Organização', p.organization ? ORGANIZATIONS[p.organization] : null],
    ['Categoria', p.category ? CATEGORIES[p.category] : null],
    ['Site', p.website],
    ['Telefone', p.phone],
    [
      'Endereço',
      [
        p.address,
        p.addressNumber,
        p.complement,
        p.district,
        p.city && `${p.city}/${p.state}`,
        p.postalCode,
      ]
        .filter(Boolean)
        .join(', '),
    ],
    ['Responsável', [p.contactName, p.contactRole].filter(Boolean).join(' · ')],
    ['Contato', [p.contactEmail, p.contactPhone].filter(Boolean).join(' · ')],
    ['Cliente no Asaas', p.asaasCustomerId],
  ];
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Dados da instituição
        </Typography>
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '160px minmax(0, 1fr)' },
            rowGap: 0.75,
            columnGap: 2,
          }}
        >
          {rows
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <Box key={label} sx={{ display: 'contents' }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                  {value}
                </Typography>
              </Box>
            ))}
        </Box>
        {billing.profile.website && (
          <Chip
            size="small"
            component="a"
            clickable
            href={billing.profile.website}
            target="_blank"
            rel="noopener"
            label="Abrir site"
            sx={{ mt: 1.5 }}
          />
        )}
      </CardContent>
    </Card>
  );
}
